import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Calendar, BookOpen, Users, Mail, ClipboardCheck, FolderOpen, CalendarDays, Shirt, UserPlus, AlertTriangle, Image, Sparkles, ShoppingBag, Instagram, Facebook } from 'lucide-react';
import { useState } from 'react';

export const Navbar = () => {
  const { user, isAuthenticated, logout, isAdmin, isStaff, isTeacher, isAdminOrStaff } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (isAdmin || isStaff) return '/admin';
    if (isTeacher) return '/teacher';
    return '/dashboard';
  };

  return (
    <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
      {/* SVG gradient defs (used by lucide social icons in nav + footer) */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="upaSocialGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF007F" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
      </svg>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 ml-2 sm:ml-4" data-testid="nav-logo">
            <img 
              src="/favicon-512.png?v=3" 
              alt="UPA — Universal Performing Arts" 
              className="h-14 w-14 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/classes" 
              className="text-white/70 hover:text-[#FF007F] transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-classes"
            >
              Classes
            </Link>
            <Link 
              to="/schedule" 
              className="text-white/70 hover:text-[#FF007F] transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-schedule"
            >
              Schedule
            </Link>
            <Link 
              to="/about" 
              className="text-white/70 hover:text-[#FF007F] transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-about"
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="text-white/70 hover:text-[#FF007F] transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-contact"
            >
              Contact
            </Link>
            <Link 
              to="/bootcamps" 
              className="text-white/70 hover:text-purple-400 transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-bootcamps"
            >
              Camps
            </Link>
            <Link 
              to="/show-choir" 
              className="text-white/70 hover:text-[#7000FF] transition-colors font-semibold uppercase tracking-wide text-sm whitespace-nowrap"
              data-testid="nav-show-choir"
            >
              Show Choir
            </Link>
            <Link 
              to="/primatotterinas" 
              className="text-white/70 hover:text-[#FF007F] transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-primatotterinas"
            >
              Primatotterinas
            </Link>
            <a 
              href="https://soul2soledancewear.company.site/products/upa" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-emerald-400 transition-colors font-semibold uppercase tracking-wide text-sm"
              data-testid="nav-shop"
            >
              Shop
            </a>

            {/* Social icons — large screens only (footer covers small) */}
            <div className="hidden xl:flex items-center gap-2 ml-1">
              <a
                href="https://www.instagram.com/upa_school/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="UPA on Instagram"
                data-testid="nav-social-instagram"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <Instagram className="w-5 h-5" style={{ stroke: 'url(#upaSocialGrad)' }} />
              </a>
              <a
                href="https://www.facebook.com/upaschool"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="UPA on Facebook"
                data-testid="nav-social-facebook"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <Facebook className="w-5 h-5" style={{ stroke: 'url(#upaSocialGrad)' }} />
              </a>
            </div>
            
            <Link 
              to="/free-trial" 
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white px-6 py-2 font-bold uppercase tracking-wide text-sm transition-colors"
              data-testid="nav-enroll"
            >
              Book Trial
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-white hover:text-white hover:bg-white/10" data-testid="nav-user-menu">
                    <div className="w-8 h-8 bg-[#FF007F]/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-[#FF007F]" />
                    </div>
                    <span className="font-medium">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#111] border-[#333] text-white">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-white/50 capitalize">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-[#333]" />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  {isAdminOrStaff && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin/classes')} data-testid="nav-manage-classes">
                        <BookOpen className="mr-2 h-4 w-4" />
                        Manage Classes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/messages')} data-testid="nav-messages">
                        <Mail className="mr-2 h-4 w-4" />
                        Messages & Notices
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/daily-register')} data-testid="nav-register-attendance">
                        <ClipboardCheck className="mr-2 h-4 w-4" />
                        Daily Register
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/media-drop')} data-testid="nav-media-drop">
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Media Drop
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/site-media')} data-testid="nav-site-media">
                        <Image className="mr-2 h-4 w-4" />
                        Site Media
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="nav-manage-users">
                        <Users className="mr-2 h-4 w-4" />
                        Manage Users
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/bootcamps')} data-testid="nav-bootcamp-admin" className="text-purple-400">
                        <Sparkles className="mr-2 h-4 w-4" />
                        Bootcamp Admin
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <DropdownMenuItem onClick={() => navigate('/term-dates')} data-testid="nav-term-dates">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        Term Dates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/uniform')} data-testid="nav-uniform">
                        <Shirt className="mr-2 h-4 w-4" />
                        Uniform
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <DropdownMenuItem onClick={() => navigate('/admin/students/import')} data-testid="nav-student-import">
                        <UserPlus className="mr-2 h-4 w-4" />
                        Import Students
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/accident-reports')} data-testid="nav-accident-reports">
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Accident Reports
                      </DropdownMenuItem>
                    </>
                  )}
                  {isTeacher && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/teacher')} data-testid="nav-my-schedule">
                        <Calendar className="mr-2 h-4 w-4" />
                        My Schedule
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/daily-register')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-teacher-register">
                        <ClipboardCheck className="mr-2 h-4 w-4" />
                        Daily Register
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/media-drop')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-teacher-media-drop">
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Media Drop
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/accident-reports')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-teacher-accident-reports">
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Accident Reports
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <DropdownMenuItem onClick={() => navigate('/term-dates')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-teacher-term-dates">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        Term Dates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/uniform')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-teacher-uniform">
                        <Shirt className="mr-2 h-4 w-4" />
                        Uniform
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'parent' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/accident-reports')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-parent-accident-reports">
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Accident Reports
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/term-dates')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-parent-term-dates">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        Term Dates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/uniform')} className="hover:bg-white/10 focus:bg-white/10" data-testid="nav-parent-uniform">
                        <Shirt className="mr-2 h-4 w-4" />
                        Uniform
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#333]" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10" data-testid="nav-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="nav-login">
                  Log in
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-2">
              <Link 
                to="/classes" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Classes
              </Link>
              <Link 
                to="/schedule" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Schedule
              </Link>
              <Link 
                to="/about" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/bootcamps" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Camps
              </Link>
              <Link 
                to="/show-choir" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Show Choir
              </Link>
              <Link 
                to="/primatotterinas" 
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Primatotterinas
              </Link>
              <a 
                href="https://soul2soledancewear.company.site/products/upa" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingBag className="w-4 h-4" /> Shop
              </a>
              
              <div className="border-t border-white/10 mt-2 pt-2">
                <Link 
                  to="/free-trial" 
                  className="px-4 py-2 bg-[#FF007F] text-white rounded-lg text-center font-bold uppercase block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Book Trial
                </Link>

                {/* Mobile social links */}
                <div className="flex items-center justify-center gap-4 mt-4">
                  <a
                    href="https://www.instagram.com/upa_school/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="UPA on Instagram"
                    data-testid="mobile-social-instagram"
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors"
                  >
                    <Instagram className="w-5 h-5" style={{ stroke: 'url(#upaSocialGrad)' }} />
                  </a>
                  <a
                    href="https://www.facebook.com/upaschool"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="UPA on Facebook"
                    data-testid="mobile-social-facebook"
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors"
                  >
                    <Facebook className="w-5 h-5" style={{ stroke: 'url(#upaSocialGrad)' }} />
                  </a>
                </div>
              </div>
              
              {isAuthenticated && (
                <div className="border-t border-white/10 mt-2 pt-2">
                  <p className="px-4 py-1 text-xs text-white/40 uppercase tracking-wider">
                    {user?.first_name} ({user?.role})
                  </p>
                  <Link 
                    to={getDashboardLink()} 
                    className="px-4 py-2 text-white hover:bg-white/10 rounded-lg flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </Link>
                  
                  {/* Admin/Staff Mobile Menu Items */}
                  {isAdminOrStaff && (
                    <>
                      <p className="px-4 py-1 mt-2 text-xs text-[#FF007F]/60 uppercase tracking-wider">
                        Admin Tools
                      </p>
                      <Link 
                        to="/admin/classes" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <BookOpen className="w-4 h-4" /> Manage Classes
                      </Link>
                      <Link 
                        to="/admin/students" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Users className="w-4 h-4" /> Students
                      </Link>
                      <Link 
                        to="/admin/messages" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Mail className="w-4 h-4" /> Messages & Notices
                      </Link>
                      <Link 
                        to="/daily-register" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <ClipboardCheck className="w-4 h-4" /> Daily Register
                      </Link>
                      <Link 
                        to="/media-drop" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <FolderOpen className="w-4 h-4" /> Media Drop
                      </Link>
                      <Link 
                        to="/admin/site-media" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Image className="w-4 h-4" /> Site Media
                      </Link>
                      <Link 
                        to="/admin/users" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Users className="w-4 h-4" /> Manage Users
                      </Link>
                      <Link 
                        to="/admin/bootcamps" 
                        className="px-4 py-2 text-purple-400 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Sparkles className="w-4 h-4" /> Bootcamp Admin
                      </Link>
                      <Link 
                        to="/admin/students/import" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <UserPlus className="w-4 h-4" /> Import Students
                      </Link>
                      <Link 
                        to="/accident-reports" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <AlertTriangle className="w-4 h-4" /> Accident Reports
                      </Link>
                    </>
                  )}
                  
                  {/* Teacher Mobile Menu Items */}
                  {isTeacher && !isAdminOrStaff && (
                    <>
                      <p className="px-4 py-1 mt-2 text-xs text-[#FF007F]/60 uppercase tracking-wider">
                        Teacher Tools
                      </p>
                      <Link 
                        to="/teacher" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Calendar className="w-4 h-4" /> My Schedule
                      </Link>
                      <Link 
                        to="/daily-register" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <ClipboardCheck className="w-4 h-4" /> Daily Register
                      </Link>
                      <Link 
                        to="/media-drop" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <FolderOpen className="w-4 h-4" /> Media Drop
                      </Link>
                      <Link 
                        to="/accident-reports" 
                        className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <AlertTriangle className="w-4 h-4" /> Accident Reports
                      </Link>
                    </>
                  )}
                  
                  {/* Common items for all logged-in users */}
                  <div className="border-t border-white/10 mt-2 pt-2">
                    <Link 
                      to="/term-dates" 
                      className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <CalendarDays className="w-4 h-4" /> Term Dates
                    </Link>
                    <Link 
                      to="/uniform" 
                      className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Shirt className="w-4 h-4" /> Uniform
                    </Link>
                  </div>
                  
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="px-4 py-2 mt-2 text-left text-red-400 hover:bg-red-500/10 rounded-lg flex items-center gap-2 w-full"
                  >
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              )}
              
              {!isAuthenticated && (
                <div className="border-t border-white/10 mt-2 pt-2">
                  <Link 
                    to="/login" 
                    className="px-4 py-2 text-white/70 hover:bg-white/10 rounded-lg block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
